/**
 * CombatHUD — health, abilities, alive count, match clock, live pot.
 *
 * Every number here is a mirror of server state. The HUD has no path to compute
 * a health value, a cooldown, or a pot — it reads the snapshot, the Schema and
 * the room's `result` message. Cooldowns in particular are ABSOLUTE SERVER
 * TICKS on the wire (`PlayerSnapshot.cooldowns`), so the remaining time is
 * `(readyTick - currentServerTick) / TICK_HZ` — never a local countdown timer,
 * which would drift and eventually show "ready" on a button the server refuses.
 *
 * The pot is a wei string, formatted only here, at the UI boundary, and never
 * parsed back into a number for anything.
 */

import { CHARACTERS, CONTROL_SLOTS, TICK_HZ } from '@chaos/protocol';
import { DASH_COOLDOWN_MS } from '@chaos/sim';
import { abilityCopy } from './abilityText.js';
import { abilityArtUrl } from './abilityArt.js';
import { escapeHtml } from './html.js';
import { isTouch } from '../config.js';
import {
  setAbilityIcons,
  setCooldown as setTouchCooldown,
  setUltCharged,
} from '../input/MobileControls.js';
import { press, release } from '../input/InputManager.js';
import { playUltReadySound } from '../audio/SoundManager.js';

/**
 * Key labels come from `CONTROL_SLOTS` in the protocol, which the shell's
 * character select reads too. They used to be written out here and again there,
 * in a different app and a different language, with only a comment claiming
 * they matched — so rebinding dash was two edits and one of them got forgotten.
 *
 * The glyph and the action are this HUD's own business and stay here.
 */
const GLYPH = { basic: 'ATK', skill1: '1', skill2: '2', ultimate: 'ULT', dash: 'DSH' };

const slotFor = (slot) => {
  const control = CONTROL_SLOTS.find((c) => c.slot === slot);
  return { slot, action: slot, key: control?.key ?? '', glyph: GLYPH[slot] };
};

const SLOTS = ['basic', 'skill1', 'skill2', 'ultimate'].map(slotFor);

const DASH_SLOT = slotFor('dash');



export class CombatHUD {
  constructor() {
    this.root = document.getElementById('combat-hud');
    this.characterId = 'volt';
    this._ultWasReady = false;
    /** The bar slot currently held by the mouse, so a release outside it lands. */
    this._pointerHeld = null;
    this._build();
  }

  _build() {
    // The clock has no label: it is the most-read number in the game and the
    // biggest chip, which is label enough. The pot is the one gold fill.
    this.root.innerHTML = `
      <div class="hud-top">
        <div class="hud-chip alive"><span class="label">Alive</span><span class="value" data-alive>8</span></div>
        <div class="hud-chip clock"><span class="value" data-clock>4:00</span></div>
        <div class="hud-chip pot"><span class="label">Pot</span><span class="value" data-pot>—</span></div>
      </div>
      <div class="ability-row" data-abilities></div>
      <div class="ability-tip" data-tip hidden></div>
    `;

    this.aliveEl = this.root.querySelector('[data-alive]');
    this.clockEl = this.root.querySelector('[data-clock]');
    this.clockChip = this.root.querySelector('.hud-chip.clock');
    this.potEl = this.root.querySelector('[data-pot]');

    this.tipEl = this.root.querySelector('[data-tip]');

    const row = this.root.querySelector('[data-abilities]');
    // Built here, mounted in the dock: this module still owns the row — it
    // creates it, fills it and updates it — but the bottom-centre strip is the
    // dock's to lay out. Two overlays cannot fight over it if only one thing
    // positions it.
    document.getElementById('bottom-dock')?.appendChild(row);
    this.abilityEls = new Map();
    for (const spec of [DASH_SLOT, ...SLOTS]) {
      const el = document.createElement('button');
      el.type = 'button';
      // The ultimate gets its own class: bigger, and the only control that
      // wears a cyan ring when it is ready. Size and the ring both say "this
      // is the one you were saving".
      el.className = spec.slot === 'ultimate' ? 'ability ult-slot' : 'ability';
      const charge = spec.slot === 'ultimate' ? '<span class="charge"></span>' : '';
      // `.art` is the medallion and `.glyph` is the three-letter fallback
      // underneath it. Both are always in the DOM: the art is a network
      // request, and a button that renders nothing until a webp arrives is a
      // button that renders nothing at all on a bad connection.
      el.innerHTML = `${charge}<span class="art"></span><span class="glyph">${spec.glyph}</span><span class="cd"></span><span class="cd-text"></span>`;
      this._wireAbilityButton(el, spec.action);

      // The key label is a SIBLING of the button, not a child, and that is the
      // whole fix. `.ability` is a circle with `overflow: hidden`, so a label
      // sitting 2px off the bottom was clipped to the chord width at that
      // height — about 21px of a 58px button. "F / RMB" lost both ends and read
      // as a smudge on the rim. Outside the circle nothing clips it.
      const slot = document.createElement('div');
      slot.className = 'ability-slot';
      const key = document.createElement('span');
      key.className = 'key';
      key.textContent = spec.key;
      slot.append(el, key);
      if (spec.slot === 'ultimate') {
        // The tag a charged ultimate wears. Cyan — "live and available" — and
        // outside the button so the wipe and the art can never cover it.
        this.readyTag = document.createElement('span');
        this.readyTag.className = 'ready-tag';
        this.readyTag.textContent = 'READY';
        this.readyTag.hidden = true;
        slot.appendChild(this.readyTag);
      }
      // After the slot exists, so hovering the key explains the ability too.
      this._wireTooltip(el, spec, slot);
      row.appendChild(slot);
      this.abilityEls.set(spec.action, el);
    }

    // A press that starts on a button and ends anywhere else must still release.
    window.addEventListener('mouseup', () => this._releasePointerHeld());
    window.addEventListener('blur', () => this._releasePointerHeld());
  }

  /**
   * Hover, or keyboard focus, explains the button.
   *
   * ── Why a tooltip at all ────────────────────────────────────────────────
   *
   * The bar showed five glyphs and a key hint. "1" and "2" say nothing about
   * what they do, how far they reach, whether they can be reacted to, or why
   * you would press one rather than the other — and this is a wagered match, so
   * a player guessing at their own kit is a player losing money to the UI.
   *
   * Everything in it is read from `CHARACTERS` at hover time, which is the same
   * table the SERVER resolves hits from. A tooltip that promises 9 damage in a
   * 2.6m cone while the sim deals 8 in a 3m one is worse than no tooltip,
   * because the player then plans around it.
   *
   * FOCUS AS WELL AS HOVER: these are real `<button>`s and tabbing to one must
   * explain it too, or the bar is unreadable to anyone not using a mouse.
   *
   * Touch gets nothing here on purpose. On a phone these buttons are drag-to-
   * aim controls — press, drag, release to cast — so a long-press tooltip would
   * fight the gesture that fires the ability. Mobile needs this on the character
   * screen instead, where there is room and nothing to interrupt.
   */
  _wireTooltip(el, spec, hoverTarget) {
    const show = () => this._showTip(el, spec);
    const hide = () => this._hideTip();
    // Hover is on the SLOT so the key pill counts too. It used to be inside the
    // button and moved out to stop the circle clipping it, which would
    // otherwise have carved a dead strip out of the hover target.
    hoverTarget.addEventListener('mouseenter', show);
    hoverTarget.addEventListener('mouseleave', hide);
    // Focus stays on the button: a div is not tabbable, and the reason this
    // listens for focus at all is that the bar has to explain itself to
    // somebody who never touches a mouse.
    el.addEventListener('focus', show);
    el.addEventListener('blur', hide);
  }

  _showTip(el, spec) {
    if (isTouch) return;
    const copy = abilityCopy(this.characterId, spec.slot);
    if (!copy) return;

    this.tipEl.innerHTML =
      `<div class="tip-head"><span class="tip-name">${escapeHtml(copy.name)}</span>` +
      `<span class="tip-key">${escapeHtml(spec.key)}</span></div>` +
      `<div class="tip-stats">${copy.stats.map((t) => `<span>${escapeHtml(t)}</span>`).join('')}</div>` +
      `<div class="tip-shape">${escapeHtml(copy.shape)}</div>` +
      `<p class="tip-body">${escapeHtml(copy.description)}</p>`;
    this.tipEl.hidden = false;

    // Centred over its button and clamped to the viewport, so the ultimate at
    // the end of the row does not push its own tooltip off screen.
    const r = el.getBoundingClientRect();
    const w = this.tipEl.offsetWidth;
    const margin = 10;
    const left = Math.min(
      Math.max(margin, r.left + r.width / 2 - w / 2),
      window.innerWidth - w - margin
    );
    this.tipEl.style.left = `${left}px`;
    this.tipEl.style.bottom = `${window.innerHeight - r.top + 12}px`;
  }

  _hideTip() {
    if (this.tipEl) this.tipEl.hidden = true;
  }

  /**
   * Makes one bar slot a real control.
   *
   * The bar was `pointer-events: none` divs styled as raised buttons: it looked
   * clickable and did nothing, which on desktop is the only visible affordance
   * for abilities a trackpad user cannot otherwise reach. It drives `press` and
   * `release` from InputManager — the same two functions the keyboard and the
   * touch cluster call — so a click produces a bit-identical InputFrame and the
   * server cannot tell which device sent it.
   *
   * Locked slots still dispatch. The keyboard does not gate Q on a cooldown and
   * neither does the touch cluster; the sim refuses the cast, and a bar that
   * silently swallowed the press would be a third input scheme.
   */
  _wireAbilityButton(el, action) {
    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      // InputManager fires the basic attack from a document-level `mousedown`,
      // so without this, clicking DSH would also swing. `mouseup` is left to
      // propagate: swallowing it would strand a press that began on the canvas.
      e.stopPropagation();
      // Also suppresses focus, so clicking a slot never takes the keyboard away
      // from the canvas. Tab still reaches it.
      e.preventDefault();
      this._pointerHeld = action;
      press(action);
      el.classList.add('pressed');
    });

    el.addEventListener('mouseup', () => this._releasePointerHeld());
    el.addEventListener('mouseleave', () => this._releasePointerHeld());

    el.addEventListener('keydown', (e) => {
      // Space is jump everywhere, and a focused button must not steal it into a
      // cast as well — cancelling it here stops the browser's own activation.
      // Enter is the keyboard activation.
      if (e.key === ' ') {
        e.preventDefault();
        return;
      }
      if (e.key !== 'Enter' || e.repeat) return;
      e.preventDefault();
      press(action);
      el.classList.add('pressed');
    });

    el.addEventListener('keyup', (e) => {
      if (e.key !== 'Enter') return;
      release(action);
      el.classList.remove('pressed');
    });

    el.addEventListener('blur', () => {
      release(action);
      el.classList.remove('pressed');
    });
  }

  _releasePointerHeld() {
    const action = this._pointerHeld;
    if (!action) return;
    this._pointerHeld = null;
    release(action);
    this.abilityEls.get(action)?.classList.remove('pressed');
  }

  /**
   * Paint the medallions onto the desktop row.
   *
   * The glyph stays in the DOM behind the art rather than being replaced: a
   * missing or slow webp then degrades to the old three-letter label instead of
   * an empty circle. `.has-art` is what hides it, and only once a URL exists.
   */
  _setAbilityArt(def) {
    for (const spec of [DASH_SLOT, ...SLOTS]) {
      const el = this.abilityEls.get(spec.action);
      const art = el?.querySelector('.art');
      if (!art) continue;
      const url = abilityArtUrl(def, spec.action);
      if (url) {
        art.style.backgroundImage = `url("${url}")`;
        el.classList.add('has-art');
      } else {
        art.style.backgroundImage = '';
        el.classList.remove('has-art');
      }
    }
  }

  setCharacter(characterId) {
    this.characterId = characterId;
    const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
    // Still published, because the ability buttons and the ult ring tint from
    // it — the same number that colours this character's telegraphs and VFX out
    // in the world, rather than a second palette that agrees today.
    // On the ROOT element, not on #combat-hud. #touch-layer is a SIBLING of
    // #hud, so a property set here never reached the ability buttons and they
    // fell through to the :root default — volt's cyan, for all five characters.
    document.documentElement.style.setProperty(
      '--self-color',
      `#${def.colorHex.toString(16).padStart(6, '0')}`,
    );
    // The touch buttons take their art from the same call that publishes
    // --self-color: one character change, one place that hears about it.
    setAbilityIcons(def);
    // And so does the DESKTOP row. The medallions shipped to the touch cluster
    // only, so a mouse player kept the three-letter glyphs — DSH, ATK, ULT —
    // which say nothing about what the button does. Same mapping, same call,
    // both bars.
    this._setAbilityArt(def);
    // A tooltip open across a character swap would keep describing the old kit.
    this._hideTip();
    for (const spec of SLOTS) {
      const el = this.abilityEls.get(spec.action);
      const ability = def.abilities[spec.slot];
      if (el && ability) el.title = `${ability.name} — ${ability.description}`;
    }
  }

  setPractice(practice) {
    this.potEl.closest(".pot").style.display = practice ? "none" : "";
  }

  setPot(potWei) {
    this.potEl.textContent = formatEth(potWei);
  }

  /**
   * @param {object} mine PlayerSnapshot for the local player
   * @param {number} serverTick the snapshot's tick — cooldowns are relative to it
   */
  update(mine, serverTick, match) {
    const def = CHARACTERS[mine.characterId] ?? CHARACTERS.volt;

    // Charge fills the ULT button bottom-up rather than a bar in the corner —
    // the mockups draw it that way and so does Brawl Stars, for the same
    // reason: the meter belongs on the control it unlocks, where your thumb
    // and your eye already are.
    const ultFrac = Math.max(0, Math.min(1, mine.ultCharge / 100));
    const ultReady = ultFrac >= 1;
    const ultEl = this.abilityEls.get('ultimate');
    if (ultEl) {
      ultEl.style.setProperty('--charge-pct', `${ultFrac * 100}%`);
      ultEl.classList.toggle('charged', ultReady);
    }
    if (this.readyTag) this.readyTag.hidden = !ultReady;
    if (ultReady && !this._ultWasReady) playUltReadySound();
    this._ultWasReady = ultReady;

    this.aliveEl.textContent = String(match.aliveCount);
    this.clockEl.textContent = formatClock(match.timeRemainingMs);
    this.clockChip.classList.toggle('urgent', match.timeRemainingMs <= 30_000);

    // Dash lives outside the roster table — it is universal, and the sim files
    // its cooldown under the ability id "dash".
    this._updateAbility('dash', mine.cooldowns['dash'], serverTick, DASH_COOLDOWN_MS, true);
    for (const spec of SLOTS) {
      const ability = def.abilities[spec.slot];
      const total = spec.slot === 'ultimate' ? 0 : ability.cooldownMs;
      const usable = spec.slot === 'ultimate' ? ultReady : true;
      this._updateAbility(spec.action, mine.cooldowns[ability.id], serverTick, total, usable);
    }
    // Unconditional: setUltCharged and setCooldown both no-op when the touch
    // buttons do not exist, so the isTouch test here bought nothing — and it
    // actively broke controls installed AFTER boot, since this module's isTouch
    // was captured at import and never changes. Let the setters answer.
    setUltCharged(ultReady);
  }

  _updateAbility(action, readyTick, serverTick, totalMs, usable) {
    const el = this.abilityEls.get(action);
    // The ultimate has no cooldown; its gate is charge, and a ring would imply
    // a timer that does not exist.
    const remainingMs =
      totalMs > 0 && readyTick !== undefined
        ? Math.max(0, ((readyTick - serverTick) / TICK_HZ) * 1000)
        : 0;

    if (el) {
      const ready = usable && remainingMs <= 0;
      el.classList.toggle('ready', ready);
      el.classList.toggle('locked', !ready);
      // The wipe: the fraction of the window still to run. The cover in
      // styles.css is that tall and retreats top-down.
      el.style.setProperty('--cd-left', remainingMs > 0 ? `${(remainingMs / totalMs) * 100}%` : '0%');
      const text = el.querySelector('.cd-text');
      if (text) text.textContent = remainingMs > 0 ? String(Math.ceil(remainingMs / 1000)) : '';
    }

    setTouchCooldown(action, remainingMs, totalMs); // no-ops without touch buttons
  }
}

function formatClock(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Wei -> a short ETH string, for display only.
 *
 * BigInt division throughout: `Number(wei) / 1e18` loses precision above 2^53
 * and is exactly the float-at-18-decimals mistake the money package exists to
 * avoid. Nothing reads this value back.
 */
function formatEth(weiString) {
  let wei;
  try {
    wei = BigInt(weiString ?? '0');
  } catch {
    return '—';
  }
  if (wei === 0n) return '—';

  const ONE = 10n ** 18n;
  const whole = wei / ONE;
  const frac = ((wei % ONE) * 10000n) / ONE;
  return `${whole}.${String(frac).padStart(4, '0')} Ξ`;
}

export { formatEth };

